import { flatten, uniq } from 'lodash-es';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { NimbusPermissionSet } from '@nimbus-global/features/permissions/models/enums/nimbus-permission-set.enum';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { NimbusLicense } from '../../shared/services/domain/models/license.model';
import { PermissionSetModel } from './permission-set.model';

export class PermissionsModel {
  public static Empty: PermissionsModel = new PermissionsModel({}, [], {}, [], [], [], {}, []);
  public readonly teamIds: string[];
  public readonly tenantIds: string[];
  public readonly organizationUnitIds: string[];

  /**
   *
   * @param teams
   * @param forAllTeams
   * @param tenants
   * @param forAllTenants
   * @param permissionSets
   * @param licenseTypes
   * @param organizationUnits
   * @param forAllOrganizationUnits
   */
  public constructor(
    /**
     * Set of permission by TeamId
     */
    public readonly teams: IMap<PermissionSetModel[]>,
    /**
     * Aggregated list of permissions for Teams
     */
    public readonly forAllTeams: PermissionSetModel[],
    /**
     * Set of permission by TenantId
     */
    public readonly tenants: IMap<PermissionSetModel[]>,
    /**
     * Aggregated list of permissions for Tenants
     */
    public readonly forAllTenants: PermissionSetModel[],
    /**
     * Aggregated permissions for all entities
     */
    public readonly permissionSets: PermissionSetModel[],
    /**
     * Assigned license types
     */
    public readonly licenseTypes: NimbusLicense[],
    /**
     * Set of permission by OrganizationUnitIds
     */
    public readonly organizationUnits: IMap<PermissionSetModel[]>,
    /**
     * Aggregated list of permissions for OrganizationUnits
     */
    public readonly forAllOrganizationUnits: PermissionSetModel[],
  ) {
    this.teamIds = Object.keys(teams);
    this.tenantIds = Object.keys(tenants);
    this.organizationUnitIds = Object.keys(organizationUnits);
  }

  /**
   * returns true if the aggregated permission list has the given permission
   * @param permission
   */
  public hasPermission(permission: NimbusPermission): boolean {
    return this.permissionSets.some((ps) => ps.permissions.some((p) => p === permission));
  }

  /**
   * returns true if the roles list has the given role. WARNING: avoid this call if its possible we will stop using PermissionSets!
   * @param permissionSet
   */
  public hasPermissionSet(permissionSet: NimbusPermissionSet): boolean {
    return this.permissionSets.some((ps) => ps.permissionSet === permissionSet);
  }

  /**
   * returns true if the roles list has at least one custom role
   */
  public hasCustomRole(): boolean {
    return this.permissionSets.some((ps) => this.isCustomRole(ps.permissionSet));
  }

  /**
   * returns true if the roles list has at least one custom role for Service (with the ServicesRead permission)
   */
  public hasCustomRoleForService(teamId: string): boolean {
    const isCustomRoleForServicePredicate = (ps: PermissionSetModel): boolean =>
      this.isCustomRole(ps.permissionSet) && ps.permissions?.includes(NimbusPermission.ServicesRead);
    return (
      (!!this.forAllTeams && this.forAllTeams.some(isCustomRoleForServicePredicate)) ||
      (!!this.teams[teamId] && this.teams[teamId].some(isCustomRoleForServicePredicate))
    );
  }

  /**
   * returns true if the team has the given permission set. WARNING: avoid this call if its possible we will stop using PermissionSets!
   * @param teamId
   * @param permissionSet
   */
  public hasTeamPermissionSet(teamId: string, permissionSet: NimbusPermissionSet): boolean {
    return (
      (!!this.forAllTeams && this.forAllTeams.some((ps) => ps.permissionSet === permissionSet)) ||
      (!!this.teams[teamId] && this.teams[teamId].some((ps) => ps.permissionSet === permissionSet))
    );
  }
  /**
   * returns true if the team has the given permission.
   * @param teamId
   * @param permission
   */
  public hasTeamPermission(teamId: string, permission: NimbusPermission): boolean {
    return !!this.teams[teamId] && this.teams[teamId].some((ps) => ps?.permissions?.includes(permission));
  }
  /**
   * returns true if the all teams have the given permission.
   * @param teamIds
   * @param permission
   */
  public hasAllTeamsPermission(teamIds: string[], permission: NimbusPermission): boolean {
    return teamIds.every((teamId) => this.teams[teamId]?.some((ps) => ps?.permissions?.includes(permission)));
  }
  /**
   * returns true if the tenant has the given permission set. WARNING: avoid this call if its possible we will stop using PermissionSets!
   * @param tenantId
   * @param permissionSet
   */
  public hasTenantPermissionSet(tenantId: string, permissionSet: NimbusPermissionSet): boolean {
    return (
      (!!this.forAllTenants && this.forAllTenants.some((ps) => ps.permissionSet === permissionSet)) ||
      (!!this.tenants[tenantId] && this.tenants[tenantId].some((ps) => ps.permissionSet === permissionSet))
    );
  }

  /**
   * returns true if the aggregated permission list has the given permission
   * @param permission
   */
  public isGranted(permission: NimbusPermission): boolean {
    // TODO: duplicated functionality (hasPermission)
    return this.permissionSets.some((ps) => ps.permissions?.includes(permission));
  }

  /**
   * returns true if the aggregated permission list has all the given permissions
   * @param permissions
   */
  public areGranted(permissions: NimbusPermission[]): boolean {
    return this.permissionSets.some((ps) => permissions.every((p) => ps.permissions?.includes(p)));
  }

  /**
   * Check if the permission granted for any resource
   * @param resourceId
   * @param permission
   */
  public isGrantedForResource(resourceId: string, permission: NimbusPermission): boolean {
    return (
      this.isGrantedForTenant(resourceId, permission) ||
      this.isGrantedForOrganizationUnit(resourceId, permission) ||
      this.isGrantedForTeam(resourceId, permission)
    );
  }

  /**
   * Check if the permission granted for tenant
   * @param tenantId
   * @param permission
   */
  public isGrantedForTenant(tenantId: string, permission: NimbusPermission): boolean {
    return (
      (!!this.forAllTenants && this.forAllTenants.some((ps) => ps.permissions?.includes(permission))) ||
      (!!this.tenants[tenantId] && this.tenants[tenantId].some((ps) => ps.permissions?.includes(permission)))
    );
  }

  /**
   * Check if the permission granted for organization Unit
   * @param organizationUnitId
   * @param permission
   */
  public isGrantedForOrganizationUnit(organizationUnitId: string, permission: NimbusPermission): boolean {
    return (
      (!!this.forAllOrganizationUnits && this.forAllOrganizationUnits.some((ps) => ps.permissions?.includes(permission))) ||
      (!!this.organizationUnits[organizationUnitId] &&
        this.organizationUnits[organizationUnitId].some((ps) => ps.permissions?.includes(permission)))
    );
  }

  /**
   * Check if the permission granted for any team
   * @param teamId
   * @param permission
   */
  public isGrantedForTeam(teamId: string, permission: NimbusPermission): boolean {
    return (
      (!!this.forAllTeams && this.forAllTeams.some((ps) => ps.permissions?.includes(permission))) ||
      (!!this.teams[teamId] && this.teams[teamId].some((ps) => ps.permissions?.includes(permission)))
    );
  }

  /**
   * Check if the permissions granted for any team
   * @param teamId
   * @param permissions
   */
  public areGrantedForTeam(teamId: string, permissions: NimbusPermission[]): boolean {
    const rolesForTeam = [...(this.forAllTeams ?? []), ...(this.teams[teamId] ?? [])];
    return rolesForTeam.some((ps) => permissions.every((p) => ps.permissions?.includes(p)));
  }

  /**
   * Check if the permissions granted for any resource
   * @param resourceId
   * @param permissions
   */
  public areGrantedForResource(resourceId: string, permissions: NimbusPermission[]): boolean {
    return this.getPermissionSetsByResourceId(resourceId).some((ps) => permissions.every((p) => ps.permissions?.includes(p)));
  }

  /**
   * Check the given license has added
   * @param license
   */
  public hasLicense(license: NimbusLicense): boolean {
    return !!this.licenseTypes && this.licenseTypes.includes(license);
  }

  /**
   * Get list of permission by Id
   * @param id
   */
  public getPermissionsEntity(id: string): NimbusPermission[] {
    return uniq(flatten(this.getPermissionSetsByResourceId(id).map((s) => s.permissions)));
  }

  /**
   * Get list of permission sets by Id
   * @param id
   */
  public getPermissionSetsEntity(id: string): NimbusPermissionSet[] {
    return uniq(flatten(this.getPermissionSetsByResourceId(id).map((s) => s.permissionSet)));
  }

  public isCustomRole(permissionSet: NimbusPermissionSet): boolean {
    const customRolesPrefixes: string[] = ['custom-roles/', 'parent_custom-roles/'];
    return customRolesPrefixes.some((x) => permissionSet.startsWith(x));
  }

  /**
   * Get the permission sets (roles) by resource Id (tenant, team, or organization unit).
   * @param resourceId
   */
  private getPermissionSetsByResourceId(resourceId: string): PermissionSetModel[] {
    return this.tenants[resourceId] || this.teams[resourceId] || this.organizationUnits[resourceId] || [];
  }
}
